import { isArray } from 'lodash';
import { authHeaders, client } from '../../_shared/services/openapiClient';
import type { paths } from '../../../types/schema';

export type AdaptedFictionPreviewResponse =
  paths['/api/v1/adapted_fiction_previews']['get']['responses']['200']['content']['application/json']['adaptedFictionPreviews'];

export type AdaptedFictionPreviewDetailResponse =
  paths['/api/v1/adapted_fiction_previews/{id}']['get']['responses']['200']['content']['application/json']['adaptedFictionPreview'];

export type CreateCastRelationshipBody = NonNullable<
  paths['/api/v1/cast_relationships']['post']['requestBody']
>['content']['application/json'];

export type CreateCastRelationshipResponse =
  paths['/api/v1/cast_relationships']['post']['responses']['201']['content']['application/json']['castRelationship'];

export type CreateSceneBody = NonNullable<
  paths['/api/v1/scenes']['post']['requestBody']
>['content']['application/json'];
export type CreateSceneResponse =
  paths['/api/v1/scenes']['post']['responses']['201']['content']['application/json']['scene'];

export type SceneContentsResponse =
  paths['/api/v1/scenes/{scene_id}/contents']['get']['responses']['200']['content']['application/json']['sceneContents'];

export type SceneContentsPageResponse =
  paths['/api/v1/scenes/{scene_id}/contents']['get']['responses']['200']['content']['application/json'];

export type CastRelationshipResponse =
  paths['/api/v1/cast_relationships/{id}']['get']['responses']['200']['content']['application/json']['castRelationship'];

export type SceneDetailResponse =
  paths['/api/v1/scenes/{id}']['get']['responses']['200']['content']['application/json']['scene'];

export type UserSceneSubscriptionResponse =
  paths['/api/v1/user_scene_subscriptions']['get']['responses']['200']['content']['application/json']['userSceneSubscriptions'];

export type UserSceneSubscriptionsPageResponse =
  paths['/api/v1/user_scene_subscriptions']['get']['responses']['200']['content']['application/json'];

export type SceneOriginalEpisodesResponse =
  paths['/api/v1/scenes/{scene_id}/episodes']['get']['responses']['200']['content']['application/json']['sceneEpisodes'];

export type SceneOriginalEpisodesParams =
  paths['/api/v1/scenes/{scene_id}/episodes']['get']['parameters']['query'];

export type SceneOriginalEpisodesPageResponse =
  paths['/api/v1/scenes/{scene_id}/episodes']['get']['responses']['200']['content']['application/json'];

export type SceneAllParagraphsResponse =
  paths['/api/v1/scenes/{scene_id}/episodes/all_paragraphs']['get']['responses']['200']['content']['application/json'];

export type SceneOriginalEpisodeParagraphsParams =
  paths['/api/v1/scenes/{scene_id}/episodes/{episode_id}/paragraphs']['get']['parameters']['query'];
export type SceneOriginalEpisodeParagraphsPageResponse =
  paths['/api/v1/scenes/{scene_id}/episodes/{episode_id}/paragraphs']['get']['responses']['200']['content']['application/json'];

export type UpdateReadingProgressBody = NonNullable<
  paths['/api/v1/scenes/{scene_id}/reading_progress']['patch']['requestBody']
>['content']['application/json'];
export type UpdateReadingProgressResponse =
  paths['/api/v1/scenes/{scene_id}/reading_progress']['patch']['responses']['200']['content']['application/json'];

export async function getAdaptedFictionPreviews({
  page,
  perPage,
  castRelationshipId
}: {
  page: number;
  perPage: number;
  castRelationshipId: string;
}): Promise<AdaptedFictionPreviewResponse> {
  const { data, error } = await client.GET('/api/v1/adapted_fiction_previews', {
    headers: authHeaders(),
    params: {
      query: {
        page,
        per_page: perPage,
        cast_relationship_id: castRelationshipId
      }
    }
  });

  if (error) throw error;
  if (!data) throw new Error('Empty response when getting adapted fiction previews');
  return data.adaptedFictionPreviews;
}

export async function getAdaptedFictionPreviewDetail({
  adaptedFictionPreviewId,
  castRelationshipId
}: {
  adaptedFictionPreviewId: string;
  castRelationshipId: string;
}): Promise<AdaptedFictionPreviewDetailResponse> {
  const { data, error } = await client.GET('/api/v1/adapted_fiction_previews/{id}', {
    headers: authHeaders(),
    params: {
      path: { id: adaptedFictionPreviewId },
      query: { cast_relationship_id: castRelationshipId }
    }
  });

  if (error) throw error;
  if (!data) throw new Error('Empty response when getting adapted fiction preview detail');
  return data.adaptedFictionPreview;
}

export async function createScene(body: CreateSceneBody): Promise<CreateSceneResponse> {
  const { data, error } = await client.POST('/api/v1/scenes', {
    headers: authHeaders(),
    body
  });

  if (error) throw error;
  if (!data) throw new Error('Empty response when creating scene');
  return data.scene;
}

const DEFAULT_CONTENTS_LIMIT = 1;

export async function getSceneContents({
  sceneId,
  scenePreviewId,
  cursor,
  direction = 'after',
  limit = DEFAULT_CONTENTS_LIMIT
}: {
  sceneId: string;
  scenePreviewId: string;
  cursor?: string;
  direction?: 'after' | 'before';
  limit?: number;
}): Promise<SceneContentsPageResponse> {
  const { data, error } = await client.GET('/api/v1/scenes/{scene_id}/contents', {
    headers: authHeaders(),
    params: {
      path: { scene_id: sceneId },
      query: { cursor: cursor, direction, limit, adapted_fiction_preview_id: scenePreviewId }
    }
  });

  if (error) throw error;
  if (!data || !('sceneContents' in data))
    throw new Error('Empty response when getting scene contents');
  return data;
}

export async function getSceneDetail({
  sceneId
}: {
  sceneId: string;
}): Promise<SceneDetailResponse> {
  const { data, error } = await client.GET('/api/v1/scenes/{id}', {
    headers: authHeaders(),
    params: { path: { id: sceneId } }
  });

  if (error) throw error;
  if (!data) throw new Error('Empty response when getting scene detail');
  return data.scene;
}

export async function getSceneSubscriptions({
  page,
  perPage,
  sceneContentType,
  likedOnly = false
}: {
  page: number;
  perPage: number;
  sceneContentType:
    | 'SceneChat'
    | 'SceneThread'
    | 'SceneEpisode'
    | ('SceneChat' | 'SceneThread' | 'SceneEpisode')[];
  likedOnly?: boolean;
}): Promise<UserSceneSubscriptionsPageResponse> {
  const { data, error } = await client.GET('/api/v1/user_scene_subscriptions', {
    headers: authHeaders(),
    params: {
      query: {
        page,
        per_page: perPage,
        // @ts-ignore
        scene_content_type: isArray(sceneContentType)
          ? sceneContentType.join(',')
          : sceneContentType,
        liked_only: likedOnly
      }
    }
  });

  if (error) throw error;
  if (!data) throw new Error('Empty response when getting scene subscriptions');
  return data;
}

export async function getSceneOriginalEpisodes({
  sceneId,
  params
}: {
  sceneId: string;
  params?: SceneOriginalEpisodesParams;
}): Promise<SceneOriginalEpisodesPageResponse> {
  const { data, error } = await client.GET('/api/v1/scenes/{scene_id}/episodes', {
    headers: authHeaders(),
    params: { path: { scene_id: sceneId }, query: params }
  });

  if (error) throw error;
  if (!data) throw new Error('Empty response when getting scene original episodes');
  return data;
}

export async function getSceneAllParagraphs(sceneId: string): Promise<SceneAllParagraphsResponse> {
  const { data, error } = await client.GET('/api/v1/scenes/{scene_id}/episodes/all_paragraphs', {
    headers: authHeaders(),
    params: { path: { scene_id: sceneId } }
  });

  if (error) throw error;
  if (!data) throw new Error('Empty response when getting scene all paragraphs');
  return data;
}

export async function getSceneOriginalEpisodeParagraphs({
  sceneId,
  episodeId,
  params
}: {
  sceneId: string;
  episodeId: string;
  params?: SceneOriginalEpisodeParagraphsParams;
}): Promise<SceneOriginalEpisodeParagraphsPageResponse> {
  const { data, error } = await client.GET(
    '/api/v1/scenes/{scene_id}/episodes/{episode_id}/paragraphs',
    {
      headers: authHeaders(),
      params: { path: { scene_id: sceneId, episode_id: episodeId }, query: params }
    }
  );

  if (error) throw error;
  if (!data) throw new Error('Empty response when getting scene original episode paragraphs');
  return data;
}

export async function updateReadingProgress({
  sceneId,
  body
}: {
  sceneId: string;
  body: UpdateReadingProgressBody;
}): Promise<UpdateReadingProgressResponse> {
  const { data, error } = await client.PATCH('/api/v1/scenes/{scene_id}/reading_progress', {
    headers: authHeaders(),
    params: { path: { scene_id: sceneId } },
    body
  });

  if (error) throw error;
  if (!data) throw new Error('Empty response when updating reading progress');
  return data;
}
